import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Button, Form, Row, Col, Alert, Spinner } from "react-bootstrap";
import { getEstaciones } from "../helpers/rutaEstaciones";
import { postSaldoInicialEstacion } from "../helpers/rutaSaldoInicialEstacion";

const num = (v) => Number(v || 0) || 0;

const nowLocalDateTimeInput = () => {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const getEstacionBase = (e) =>
  [e?.estacion, e?.lugar].filter(Boolean).join(" ").trim().toUpperCase();

const getEstacionLabel = (e) => {
  const nombre = [e?.estacion, e?.lugar].filter(Boolean).join(" - ").trim().toUpperCase();
  return e?.codest ? `${nombre} - ${e.codest}` : nombre;
};

const normalizarEstaciones = (resp) => {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp.estaciones)) return resp.estaciones;
  if (Array.isArray(resp.data?.estaciones)) return resp.data.estaciones;
  if (Array.isArray(resp.data)) return resp.data;
  return [];
};

const errorToText = (err) => {
  if (!err) return "Ocurrió un error";
  if (typeof err === "string") return err;
  if (typeof err?.error === "string") return err.error;
  if (typeof err?.msg === "string") return err.msg;
  if (typeof err?.message === "string") return err.message;
  try {
    return JSON.stringify(err);
  } catch {
    return "Ocurrió un error";
  }
};

export default function ModalSaldoInicialEstacion({ show, setShow, onGuardado }) {
  const mountedRef = useRef(false);

  const [estaciones, setEstaciones] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const [formValues, setFormValues] = useState({
    estacion: "",
    fecha: nowLocalDateTimeInput(),
    monto: "",
    observaciones: "",
  });

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const resetForm = () => {
    setError("");
    setFormValues({
      estacion: "",
      fecha: nowLocalDateTimeInput(),
      monto: "",
      observaciones: "",
    });
  };

  const handleClose = () => {
    resetForm();
    setShow(false);
  };

  useEffect(() => {
    if (!show) return;

    let activo = true;

    const cargar = async () => {
      if (activo && mountedRef.current) {
        setCargando(true);
        setError("");
      }

      try {
        const resp = await getEstaciones();

        if (activo && mountedRef.current) {
          setEstaciones(
            normalizarEstaciones(resp)
              .filter((e) => e?.activo !== false)
              .sort((a, b) =>
                getEstacionBase(a).localeCompare(getEstacionBase(b), "es", {
                  sensitivity: "base",
                })
              )
          );
        }
      } catch (e) {
        if (activo && mountedRef.current) {
          setError("No se pudieron cargar las estaciones");
        }
      } finally {
        if (activo && mountedRef.current) {
          setCargando(false);
        }
      }
    };

    cargar();

    return () => {
      activo = false;
    };
  }, [show]);

  const handleChange = ({ target }) => {
    const { name, value } = target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formValues.estacion) {
      setError("Seleccioná una estación");
      return;
    }

    if (!formValues.fecha) {
      setError("Ingresá la fecha");
      return;
    }

    if (num(formValues.monto) <= 0) {
      setError("El monto debe ser mayor a 0");
      return;
    }

    setGuardando(true);
    setError("");

    try {
      const resp = await postSaldoInicialEstacion({
        estacion: formValues.estacion,
        fecha: formValues.fecha ? new Date(formValues.fecha).toISOString() : null,
        monto: num(formValues.monto),
        observaciones: formValues.observaciones,
      });

      if (resp?.ok === false) {
        setError(errorToText(resp));
        return;
      }

      if (onGuardado) await onGuardado(resp);
      handleClose();
    } catch (e) {
      setError(errorToText(e?.response?.data || e));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered backdrop="static">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Saldo inicial estación</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Estación</Form.Label>
                <Form.Select
                  name="estacion"
                  value={formValues.estacion}
                  onChange={handleChange}
                  disabled={cargando || guardando}
                >
                  <option value="">Seleccione estación</option>
                  {estaciones.map((e) => (
                    <option key={e._id} value={e._id}>
                      {getEstacionLabel(e)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Fecha</Form.Label>
                <Form.Control
                  type="datetime-local"
                  name="fecha"
                  value={formValues.fecha}
                  onChange={handleChange}
                  disabled={guardando}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Monto</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  name="monto"
                  value={formValues.monto}
                  onChange={handleChange}
                  disabled={guardando}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group>
                <Form.Label>Observaciones</Form.Label>
                <Form.Control
                  type="text"
                  name="observaciones"
                  value={formValues.observaciones}
                  onChange={handleChange}
                  disabled={guardando}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={guardando}>
            Cancelar
          </Button>
          <Button variant="success" type="submit" disabled={guardando}>
            {guardando ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Guardando
              </>
            ) : (
              "Guardar saldo inicial"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}